<template>
	<v-dialog v-model="dialog" max-width="560" persistent scrollable>
		<v-card class="comm-modal">
			<div class="comm-header">
				<div class="comm-header-content">
					<div class="comm-icon-wrap">
						<v-icon color="white" size="18">mdi-forum-outline</v-icon>
					</div>
					<div>
						<div class="comm-title">Canal de comunicación</div>
						<div class="comm-subtitle">
							<v-icon size="12" color="rgba(255,255,255,0.78)" class="mr-1">mdi-message-text-outline</v-icon>
							Soporte técnico y oportunidades de mejora
						</div>
					</div>
				</div>
				<v-btn icon small class="comm-close" :disabled="loading" @click="close">
					<v-icon color="rgba(255,255,255,0.8)" size="18">mdi-close</v-icon>
				</v-btn>
			</div>

			<v-card-text class="comm-body">
				<div class="comm-type-tabs">
					<div
						v-for="option in typeOptions"
						:key="option.value"
						class="comm-type-tab"
						:class="{ 'comm-type-tab--active': form.type === option.value }"
						@click="selectType(option.value)"
					>
						<v-icon
							size="20"
							:color="form.type === option.value ? option.color : '#8A93A6'"
						>
							{{ option.icon }}
						</v-icon>
						<span class="comm-type-tab-label">{{ option.label }}</span>
					</div>
				</div>

				<v-alert
					dense
					text
					:color="currentTypeOption.color"
					:icon="currentTypeOption.icon"
					class="comm-info-alert mt-4"
				>
					{{ typeInfoText }}
				</v-alert>

				<v-form ref="form" v-model="valid" @submit.prevent="submitCommunication">
					<v-select
						v-if="form.type === 'support'"
						v-model="form.category"
						:items="supportCategories"
						:rules="categoryRules"
						label="Categoría del problema"
						outlined
						dense
						required
						prepend-inner-icon="mdi-tools"
						class="mt-4"
					></v-select>

					<v-text-field
						v-model="form.title"
						:rules="titleRules"
						:label="titleLabel"
						outlined
						dense
						required
						counter="120"
						prepend-inner-icon="mdi-format-title"
						class="mt-4"
					></v-text-field>

					<v-textarea
						v-model="form.description"
						:rules="descriptionRules"
						:label="descriptionLabel"
						:hint="descriptionHint"
						persistent-hint
						outlined
						dense
						rows="4"
						auto-grow
						counter="10000"
						required
						class="mt-1"
					></v-textarea>
				</v-form>
			</v-card-text>

			<v-card-actions class="comm-footer">
				<v-btn text small color="#2D4B95" :disabled="loading" @click="close">
					Cancelar
				</v-btn>
				<v-spacer></v-spacer>
				<v-btn
					class="comm-submit-btn"
					color="#2D4B95"
					small
					:dark="valid"
					:loading="loading"
					:disabled="!valid"
					@click="submitCommunication"
				>
					<v-icon left small>mdi-send-outline</v-icon>
					Enviar
				</v-btn>
			</v-card-actions>
		</v-card>
	</v-dialog>
</template>

<script>
import { createCommunication } from '@/helpers/api/general'

export default {
	name: 'CommunicationModal',
	props: {
		value: {
			type: Boolean,
			default: false
		}
	},
	data: () => ({
		valid: false,
		loading: false,
		typeOptions: [
			{ value: 'support', label: 'Soporte técnico', icon: 'mdi-lifebuoy', color: '#2D4B95' },
			{ value: 'improvement', label: 'Oportunidad de mejora', icon: 'mdi-trending-up', color: '#3EBFA3' },
			{ value: 'module', label: 'Crea tu Módulo', icon: 'mdi-puzzle-outline', color: '#B36FE0' }
		],
		supportCategories: [
			'Error o falla del sistema',
			'Problema de acceso o permisos',
			'Lentitud o rendimiento',
			'Duda de uso',
			'Otro'
		],
		form: {
			type: 'support',
			category: null,
			title: '',
			description: ''
		}
	}),
	computed: {
		dialog: {
			get() {
				return this.value
			},
			set(value) {
				this.$emit('input', value)
			}
		},
		currentTypeOption() {
			return this.typeOptions.find(option => option.value === this.form.type) || this.typeOptions[0]
		},
		typeInfoText() {
			if (this.form.type === 'improvement') {
				return 'Cuéntanos qué mejorarías del sistema. Cada sugerencia se revisa y, si aplica, se va incorporando en próximas versiones.'
			}
			if (this.form.type === 'module') {
				return '¿Tienes una idea para mejorar LAOT.CL? Cuéntanos qué módulo o solución te gustaría integrar. Evaluaremos tu propuesta y, si aporta valor, podrás trabajar junto a nuestro equipo para desarrollarla, convertirla en una nueva solución para la comunidad e incluso ser socio de LAOT.CL.'
			}
			return 'Cuéntanos el problema técnico que estás teniendo. Tu reporte se enviará directamente a nuestro equipo de soporte.'
		},
		titleLabel() {
			if (this.form.type === 'improvement') return 'Título de la oportunidad de mejora'
			if (this.form.type === 'module') return 'Nombre de tu idea o módulo'
			return 'Título del problema'
		},
		descriptionLabel() {
			if (this.form.type === 'improvement') return 'Describe tu idea de mejora'
			if (this.form.type === 'module') return 'Describe el desarrollo que te gustaría integrar'
			return 'Descripción del problema'
		},
		descriptionHint() {
			if (this.form.type === 'improvement') return '¿Qué mejorarías y por qué? ¿A quién beneficiaría?'
			if (this.form.type === 'module') return '¿Qué haría el módulo? ¿Qué problema resuelve?'
			return '¿Qué ocurrió? ¿Cuándo, dónde y en qué pantalla?'
		},
		typeRules() {
			return [value => !!value || 'Selecciona un tipo de comunicación.']
		},
		categoryRules() {
			return [
				value => this.form.type !== 'support' || !!value || 'Selecciona una categoría.'
			]
		},
		titleRules() {
			return [
				value => !!value || 'Ingresa un título.',
				value => (value && value.length <= 120) || 'El título no puede superar los 120 caracteres.'
			]
		},
		descriptionRules() {
			return [
				value => !!value || 'Describe los detalles.',
				value => (value && value.length <= 10000) || 'La descripción no puede superar los 10.000 caracteres.'
			]
		}
	},
	watch: {
		value(value) {
			if (value) {
				this.resetForm()
			}
		}
	},
	methods: {
		selectType(value) {
			this.form.type = value
			this.form.category = null
			if (this.$refs.form) {
				this.$refs.form.resetValidation()
			}
		},
		close() {
			if (this.loading) return
			this.dialog = false
		},
		resetForm() {
			this.form = {
				type: 'support',
				category: null,
				title: '',
				description: ''
			}
			this.valid = false
			if (this.$refs.form) {
				this.$refs.form.resetValidation()
			}
		},
		async submitCommunication() {
			if (!this.$refs.form.validate()) return

			this.loading = true
			try {
				const response = await createCommunication({
					type: this.form.type,
					category: this.form.category,
					title: this.form.title,
					description: this.form.description
				})

				if (response && (response.code === 201 || response.code === 200)) {
					this.$store.globalStore.dispatch('addMessage', {
						title: 'Comunicación enviada',
						message: 'Tu mensaje fue enviado a nuestro equipo. Te responderemos a la brevedad.',
						created: new Date(),
						type: 'success',
						icon: 'mdi-check-circle',
						color: '#3EBFA3'
					})
					this.dialog = false
				}
			} catch (error) {
				this.$store.globalStore.dispatch('addMessage', {
					title: 'No se pudo enviar la comunicación',
					message: 'Ocurrió un error. Intenta nuevamente.',
					created: new Date(),
					type: 'error',
					icon: 'mdi-alert-circle',
					color: '#F4516C'
				})
			} finally {
				this.loading = false
			}
		}
	}
}
</script>

<style scoped>
.comm-modal {
	border-radius: 12px !important;
	overflow: hidden;
}
.comm-header {
	align-items: center;
	background: linear-gradient(135deg, #2D4B95 0%, #5E85EF 100%);
	display: flex;
	justify-content: space-between;
	padding: 14px 18px;
}
.comm-header-content {
	align-items: center;
	display: flex;
}
.comm-icon-wrap {
	align-items: center;
	background: rgba(255,255,255,0.16);
	border-radius: 8px;
	display: flex;
	height: 32px;
	justify-content: center;
	margin-right: 10px;
	width: 32px;
	flex-shrink: 0;
}
.comm-title {
	color: white;
	font-size: 16px;
	font-weight: 700;
	line-height: 1.2;
}
.comm-subtitle {
	align-items: center;
	color: rgba(255,255,255,0.78);
	display: flex;
	font-size: 11.5px;
	margin-top: 2px;
}
.comm-close {
	margin-right: -6px;
}
.comm-body {
	padding: 20px 20px 6px !important;
}

.comm-type-tabs {
	display: flex;
	gap: 8px;
}
.comm-type-tab {
	align-items: center;
	background: #F5F7FB;
	border: 1px solid #E8EBF3;
	border-radius: 10px;
	cursor: pointer;
	display: flex;
	flex: 1;
	flex-direction: column;
	gap: 4px;
	padding: 10px 6px;
	text-align: center;
	transition: background 0.15s ease, border-color 0.15s ease;
}
.comm-type-tab--active {
	background: #EEF2FC;
	border-color: #C9D6F5;
}
.comm-type-tab-label {
	color: #2A2E3A;
	font-size: 11.5px;
	font-weight: 600;
	line-height: 1.2;
}

.comm-info-alert {
	border-radius: 10px !important;
	font-size: 12.5px !important;
}

.comm-footer {
	border-top: 1px solid #e8ebf3;
	padding: 12px 20px !important;
}

.comm-submit-btn.v-btn--disabled {
	background-color: #DCE1EC !important;
	opacity: 1 !important;
}
.comm-submit-btn.v-btn--disabled ::v-deep .v-btn__content {
	color: #97A0B5 !important;
}
</style>